<template>
    <group-report-template  v-model:current-report-template-id="currentReportTemplateId"
                            :template-id="templateId"
                            v-model:for-group="forGroup"
                            v-model:interval="interval"
                            v-model:ran-report="reportSuccessfullySent"
                            v-model:reload-data="reloadTable"
                            v-model:running-errors="errorSending"
                            v-model:running-report="runningReport"
                            v-model:settings="settings"
                            :can-edit="permissions.can('manage_occupancy_report')"
                            :users="users"
                            :default-settings="defaultSettings"
                            :prepare-settings-function="prepareSettings"
                            :hide-title="true"
                            :use-settings-drawer="true"
                            :use-template-cards="true"
                            :use-group-reporting-option="groupReportingEnabled"
                            :has-settings="hasManualRunSettings"
                            :report-icon="settingsSchema.icon"
                            :schema="settingsSchema"
                            report-name="occupancy-report"
                            v-model:display-section="displaySection"
                            @useStandardReporting="setUseStandardReporting"
    >

        <template   #create-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="true"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #edit-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="displaySection === 'show-template' || useStandardReporting"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #run-options="props">
            <!-- Occupancy has no meters or zones to pick, so the run form is
                 only the Period card. An On-Demand run posts the picked range
                 via extraParams; a saved template's run keeps its
                 interval-derived window, ending on the anchor date. -->
            <interval-selection :use-meters="false"
                                :footer-mode="props.footerMode ?? 'none'"
                                :use-range="! props.reportTemplateId"
                                :show-anchor-date="!! props.reportTemplateId"
                                v-model:range-start="rangeStart"
                                v-model:range-end="rangeEnd"
                                v-model:end-of-interval="endOfInterval"
                                v-model:interval="interval"
                                :max-date="maxDate"
                                :min-date="minDate"
                                :is-running="runningReport"
                                :ran-report="reportSuccessfullySent"
                                :running-errors="errorSending"
                                :timezone="timezone"
                                @run-report="runReport(props.reportTemplateId ?? null)"
            ></interval-selection>
        </template>
    </group-report-template>
</template>

<script>
import {ref} from 'vue'
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";
import {useGroupReport} from "@/composables/useGroupReport";

import GroupReportTemplate from "./GroupReportTemplate";
import IntervalSelection from "./IntervalSelection";
import ReportSettingsPanel from "./settings/ReportSettingsPanel.vue";
import occupancySettingsSchema from "./settings/schemas/occupancy";
import {toReportDate} from "./settings/ReportDateRange.vue";

export default {
    name: "GroupOccupancyReport",
    mixins: [SonicuCoreMixin, ajaxTableMixin],
    components: {
        GroupReportTemplate,
        IntervalSelection,
        ReportSettingsPanel,
    },
    props: {
        // null on the report page; 'new' or a group report id on its New or
        // Edit page.
        templateId: {
            type: [String, Number],
            default: null,
        },
        interval: {
            type: String,
            default: 'weekly',
        },
        minDate: {
            type: [Date, String],
        },
        maxDate: {
            type: [Date, String],
        },
        users: {
            type: Object,
            default() {
                return {}
            }
        },
        defaultSettings: {
            type: Object,
            default() {
                return {}
            }
        },
        timezone: {
            type: String,
            default: 'America/New_York',
        },
    },
    setup(props) {
        // Occupancy has no manual-run settings (its only settings are the
        // template delivery schedule), so the composable's hasManualRunSettings
        // is false and the run form is IntervalSelection with no meters. The
        // interval posts from the base params; extraParams adds the On-Demand
        // range as plain dates (toReportDate). weekdayDefault fills a missing
        // weekday on a weekly template.
        const rangeStart = ref(null)
        const rangeEnd = ref(null)

        const report = useGroupReport(occupancySettingsSchema, {
            interval: props.interval,
            defaultSettings: props.defaultSettings,
            weekdayDefault: 1,
            extraParams: (state, reportTemplateId) => {
                if (reportTemplateId !== null) {
                    return {}
                }

                return {
                    start: toReportDate(rangeStart.value, props.timezone),
                    end: toReportDate(rangeEnd.value, props.timezone),
                }
            },
        })

        return {
            ...report,
            rangeStart,
            rangeEnd,
        }
    },
    computed: {
        settingsSchema() {
            return occupancySettingsSchema
        },
    },
}
</script>
